import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Building2,
  CalendarDays,
  Calendar,
  Check,
  CheckCircle2,
  CheckSquare,
  ChevronRight,
  Copy,
  FolderKanban,
  GitBranch,
  History,
  Layers,
  Lightbulb,
  MessageSquare,
  Package,
  Paperclip,
  Pencil,
  Send,
  Target,
  Trash2,
  User as UserIcon,
  X,
} from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { ReferenceIdBadge } from "@/components/ui/ReferenceIdBadge";
import { ConfirmDialog } from "@/components/shared/ConfirmDialog";
import { ActivityHistoryDrawer } from "@/components/audit/ActivityHistoryDrawer";
import { PhaseStagePicker } from "@/components/shared/PhaseStagePicker";
import { AssociationFields } from "@/components/shared/AssociationFields";
import { useAuth } from "@/modules/auth/hooks/useAuth";
import { useUserList } from "@/modules/users/hooks/useUsers";
import {
  useAddComment,
  useDeleteComment,
  useDeleteTask,
  useTask,
  useTaskActions,
  useTaskComments,
  useUpdateTask,
  useUpdateTaskStatus,
} from "../hooks/useTasks";
import { TaskPriorityBadge } from "../components/TaskPriorityBadge";
import { TaskStatusBadge } from "../components/TaskStatusBadge";
import { NewActionDrawer } from "@/modules/actions/components/NewActionDrawer";
import { NewMilestoneDrawer } from "@/modules/milestones/components/NewMilestoneDrawer";
import { NewTaskDrawer } from "../components/NewTaskDrawer";
import { NotesTab } from "@/modules/notes/components/NotesTab";
import { IdeasTab } from "@/modules/ideas/components/IdeasTab";
import { DecisionsTab } from "@/modules/decisions/components/DecisionsTab";
import { FileUploadZone } from "@/modules/media/components/FileUploadZone";
import type { TaskDetail, TaskPriority, TaskStatus } from "../types";

const STATUS_LIST: { value: TaskStatus; label: string }[] = [
  { value: "OPEN", label: "Open" },
  { value: "IN_PROGRESS", label: "In Progress" },
  { value: "IN_REVIEW", label: "In Review" },
  { value: "COMPLETED", label: "Completed" },
  { value: "CANCELLED", label: "Cancelled" },
];

type ItemTab =
  | "all"
  | "actions"
  | "notes"
  | "ideas"
  | "decisions"
  | "milestones";

const ITEM_TABS: { key: ItemTab; label: string }[] = [
  { key: "all", label: "All" },
  { key: "actions", label: "Actions" },
  { key: "notes", label: "Notes" },
  { key: "ideas", label: "Ideas" },
  { key: "decisions", label: "Decisions" },
  { key: "milestones", label: "Milestones" },
];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function timeAgo(iso: string): string {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} hr ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

function initialsOf(name: string): string {
  return name
    .split(" ")
    .map((n) => n[0])
    .filter(Boolean)
    .join("")
    .slice(0, 2)
    .toUpperCase();
}

function isOverdue(t: TaskDetail): boolean {
  return (
    !!t.dueDate &&
    new Date(t.dueDate) < new Date() &&
    t.status !== "COMPLETED" &&
    t.status !== "CANCELLED"
  );
}

export default function TaskDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const { data: task, isLoading } = useTask(id ?? "");
  const updateTask = useUpdateTask(id ?? "");
  const updateStatus = useUpdateTaskStatus();
  const deleteTask = useDeleteTask();
  const actionsQuery = useTaskActions(id ?? "");
  const commentsQuery = useTaskComments(id ?? "");
  const addComment = useAddComment(id ?? "");
  const deleteComment = useDeleteComment(id ?? "");
  const { data: usersResult } = useUserList({ page: 1, limit: 100 });
  const allUsers = usersResult?.data ?? [];

  const [editMode, setEditMode] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [tab, setTab] = useState<ItemTab>("all");
  const [commentText, setCommentText] = useState("");
  const [statusError, setStatusError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [actionDrawerOpen, setActionDrawerOpen] = useState(false);
  const [milestoneDrawerOpen, setMilestoneDrawerOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  // Edit-mode fields
  const [editTitle, setEditTitle] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editPriority, setEditPriority] = useState<TaskPriority>("MEDIUM");
  const [editStatus, setEditStatus] = useState<TaskStatus>("OPEN");
  const [editAssignedToId, setEditAssignedToId] = useState<string>("");
  const [editPhaseId, setEditPhaseId] = useState<string | null>(null);
  const [editStageId, setEditStageId] = useState<string | null>(null);
  const [editStartDate, setEditStartDate] = useState("");
  const [editDueDate, setEditDueDate] = useState("");
  const [editEstHours, setEditEstHours] = useState("");
  const [editCompanyId, setEditCompanyId] = useState<string | null>(null);
  const [editProductId, setEditProductId] = useState<string | null>(null);
  const [editProjectId, setEditProjectId] = useState<string | null>(null);
  const [editMeetingId, setEditMeetingId] = useState<string | null>(null);
  const [editDecisionId, setEditDecisionId] = useState<string | null>(null);
  const [editIdeaId, setEditIdeaId] = useState<string | null>(null);
  const [editMilestoneId, setEditMilestoneId] = useState<string | null>(null);

  function enterEdit(t: TaskDetail) {
    setEditTitle(t.title);
    setEditDescription(t.description ?? "");
    setEditPriority(t.priority);
    setEditStatus(t.status);
    setEditAssignedToId(t.assignedToId ?? "");
    setEditPhaseId(t.phaseId);
    setEditStageId(t.stageId);
    setEditStartDate(t.startDate ? t.startDate.slice(0, 10) : "");
    setEditDueDate(t.dueDate ? t.dueDate.slice(0, 10) : "");
    setEditEstHours(t.estimatedHours?.toString() ?? "");
    setEditCompanyId(t.companyId ?? null);
    setEditProductId(t.productId ?? null);
    setEditProjectId(t.projectId ?? null);
    setEditMeetingId(t.meetingId ?? null);
    setEditDecisionId(t.decisionId ?? null);
    setEditIdeaId(t.ideaId ?? null);
    setEditMilestoneId(t.milestoneId ?? null);
    setEditMode(true);
  }

  async function saveEdit() {
    await updateTask.mutateAsync({
      title: editTitle.trim(),
      description: editDescription.trim() || null,
      priority: editPriority,
      status: editStatus,
      assignedToId: editAssignedToId || null,
      phaseId: editPhaseId,
      stageId: editStageId,
      startDate: editStartDate ? new Date(editStartDate).toISOString() : null,
      dueDate: editDueDate ? new Date(editDueDate).toISOString() : null,
      estimatedHours: editEstHours ? Number(editEstHours) : null,
      companyId: editCompanyId,
      productId: editProductId,
      projectId: editProjectId,
      meetingId: editMeetingId,
      decisionId: editDecisionId,
      ideaId: editIdeaId,
      milestoneId: editMilestoneId,
    });
    setEditMode(false);
    showToast("Task updated");
  }

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleQuickStatus(newStatus: TaskStatus) {
    if (!task) return;
    setStatusError(null);
    const res = await updateStatus.mutateAsync({ id: task.id, status: newStatus });
    if ("incompleteActions" in res) {
      setStatusError(
        `Cannot complete — ${res.incompleteActions.incompleteCount} action(s) still open`,
      );
      return;
    }
    showToast(`Status changed to ${newStatus.replace(/_/g, " ")}`);
  }

  function handleDelete() {
    if (!task) return;
    setDeleteOpen(true);
  }

  async function confirmDelete() {
    if (!task) return;
    await deleteTask.mutateAsync(task.id);
    navigate("/tasks");
  }

  async function submitComment() {
    if (!commentText.trim()) return;
    await addComment.mutateAsync(commentText.trim());
    setCommentText("");
  }

  const actions = actionsQuery.data?.data ?? [];
  const actionStats = useMemo(() => {
    const stats = { total: actions.length, open: 0, inProgress: 0, completed: 0 };
    for (const a of actions) {
      if (a.status === "OPEN") stats.open++;
      else if (a.status === "IN_PROGRESS") stats.inProgress++;
      else if (a.status === "COMPLETED") stats.completed++;
    }
    return stats;
  }, [actions]);

  if (isLoading || !task) {
    return (
      <AppLayout
        breadcrumb={[
          { label: "PhazeX Hub", href: "/" },
          { label: "Daily Work" },
          { label: "Tasks", href: "/tasks" },
        ]}
      >
        <div className="py-12 text-center text-muted-foreground">
          {isLoading ? "Loading…" : "Task not found"}
        </div>
      </AppLayout>
    );
  }

  const overdue = isOverdue(task);
  const contextChain: {
    label: string;
    href?: string;
    icon: typeof Building2;
  }[] = [];
  if (task.companyId)
    contextChain.push({
      label: task.companyName ?? task.companyId,
      href: `/companies/${task.companyId}`,
      icon: Building2,
    });
  if (task.productId)
    contextChain.push({
      label: task.productName ?? task.productId,
      href: `/products/${task.productId}`,
      icon: Package,
    });
  if (task.projectId)
    contextChain.push({
      label: task.projectName ?? task.projectId,
      href: `/projects/${task.projectId}`,
      icon: FolderKanban,
    });
  if (task.phaseName)
    contextChain.push({
      label: task.phaseName,
      icon: Layers,
    });
  if (task.stageName)
    contextChain.push({
      label: task.stageName,
      icon: GitBranch,
    });
  if (task.meetingId)
    contextChain.push({
      label: task.meetingTitle ?? task.meetingId,
      href: `/meetings/${task.meetingId}`,
      icon: CalendarDays,
    });
  if (task.decisionId)
    contextChain.push({
      label: task.decisionTitle ?? task.decisionId,
      href: `/decisions/${task.decisionId}`,
      icon: CheckCircle2,
    });
  if (task.ideaId)
    contextChain.push({
      label: task.ideaTitle ?? task.ideaId,
      href: `/ideas/${task.ideaId}`,
      icon: Lightbulb,
    });
  if (task.milestoneId)
    contextChain.push({
      label: task.milestoneTitle ?? task.milestoneId,
      href: `/milestones/${task.milestoneId}`,
      icon: Target,
    });

  const showWarning =
    task.incompleteActionsCount > 0 &&
    task.status !== "COMPLETED" &&
    task.status !== "CANCELLED";

  return (
    <AppLayout
      breadcrumb={[
        { label: "PhazeX Hub", href: "/" },
        { label: "Daily Work" },
        { label: "Tasks", href: "/tasks" },
        { label: task.code },
      ]}
    >
      {toast && (
        <div
          className="fixed right-6 top-16 z-[9999] rounded-lg border bg-white px-4 py-3 text-sm font-medium text-green-700 shadow-lg"
          style={{ borderColor: "hsl(142,55%,80%)" }}
        >
          {toast}
        </div>
      )}

      {/* Page Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => navigate("/tasks")}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.5} />
          </button>
          <CheckSquare
            className="h-[18px] w-[18px] text-foreground"
            strokeWidth={1.5}
          />
          <h1 className="text-xl font-semibold text-foreground">View Task</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActivityOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="History"
          >
            <History
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={() => setDuplicateOpen(true)}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Duplicate this task"
          >
            <Copy
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={() => (editMode ? setEditMode(false) : enterEdit(task))}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
            title="Edit"
          >
            <Pencil
              className="h-4 w-4 text-muted-foreground"
              strokeWidth={1.5}
            />
          </button>
          <button
            type="button"
            onClick={handleDelete}
            className="flex h-8 w-8 items-center justify-center rounded hover:bg-[hsl(0,70%,96%)]"
            title="Delete"
          >
            <Trash2
              className="h-4 w-4 text-[hsl(0,70%,45%)]"
              strokeWidth={1.5}
            />
          </button>
        </div>
      </div>

      {/* ROW 1 — Summary Header */}
      <div
        className="mb-4 rounded-xl border bg-white"
        style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
      >
        {editMode ? (
          <Input
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            className="text-[20px] font-bold"
          />
        ) : (
          <div className="text-[20px] font-bold text-foreground">
            {task.title}
          </div>
        )}
        <div className="mt-2 flex flex-wrap items-center gap-2 text-[13px] text-muted-foreground">
          <ReferenceIdBadge id={task.code} />
          <TaskStatusBadge status={task.status} />
          <TaskPriorityBadge priority={task.priority} />
          {task.projectName && (
            <span className="inline-flex items-center gap-1">
              <FolderKanban className="h-3 w-3" strokeWidth={1.5} />
              {task.projectName}
            </span>
          )}
          {task.phaseName && (
            <span
              className="inline-flex rounded-full px-2 py-0.5 text-[11px]"
              style={{ background: "hsl(210,16%,95%)" }}
            >
              {task.phaseName}
            </span>
          )}
          {task.assignedToName && (
            <span className="inline-flex items-center gap-1">
              <UserIcon className="h-3 w-3" strokeWidth={1.5} />
              {task.assignedToName}
            </span>
          )}
          {task.dueDate && (
            <span
              className={`inline-flex items-center gap-1 ${
                overdue ? "text-[hsl(0,70%,45%)] font-medium" : ""
              }`}
            >
              <Calendar className="h-3 w-3" strokeWidth={1.5} />
              {formatDate(task.dueDate)}
            </span>
          )}
          {overdue && (
            <span
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase"
              style={{ background: "hsl(0,70%,94%)", color: "hsl(0,70%,40%)" }}
            >
              Overdue
            </span>
          )}
        </div>
      </div>

      {/* ROW 2 — Basic Info + Dates */}
      <div
        className="mb-4 grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        {/* LEFT — Basic Info */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center justify-between">
            <h3 className="text-[13px] font-semibold text-foreground">
              Basic Info
            </h3>
            {editMode ? (
              <div className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={saveEdit}
                  className="flex h-7 w-7 items-center justify-center rounded text-[hsl(142,72%,28%)] hover:bg-[hsl(142,55%,94%)]"
                  title="Save"
                >
                  <Check className="h-4 w-4" strokeWidth={1.5} />
                </button>
                <button
                  type="button"
                  onClick={() => setEditMode(false)}
                  className="flex h-7 w-7 items-center justify-center rounded text-muted-foreground hover:bg-[hsl(210,50%,96%)]"
                  title="Cancel"
                >
                  <X className="h-4 w-4" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => enterEdit(task)}
                className="flex h-7 w-7 items-center justify-center rounded hover:bg-[hsl(210,50%,96%)]"
                title="Edit"
              >
                <Pencil
                  className="h-3.5 w-3.5 text-muted-foreground"
                  strokeWidth={1.5}
                />
              </button>
            )}
          </div>

          <div className="mb-4 flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
            <span className="font-medium">PhazeX</span>
            {contextChain.length === 0 ? (
              <>
                <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                <span>—</span>
              </>
            ) : (
              contextChain.map((c, i) => {
                const Icon = c.icon;
                return (
                  <span key={i} className="inline-flex items-center gap-1.5">
                    <ChevronRight className="h-3 w-3" strokeWidth={1.5} />
                    <Icon className="h-3 w-3" strokeWidth={1.5} />
                    {c.href ? (
                      <Link
                        to={c.href}
                        className="hover:text-[hsl(210,100%,35%)]"
                      >
                        {c.label}
                      </Link>
                    ) : (
                      <span>{c.label}</span>
                    )}
                  </span>
                );
              })
            )}
          </div>

          <div
            className="mb-4 rounded-lg border bg-[hsl(210,30%,98%)] p-3"
            style={{ borderColor: "hsl(210,30%,88%)" }}
          >
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              Linked Context
            </div>
            <div className="grid grid-cols-2 gap-y-1.5 text-[12px]">
              <LinkedRow
                label="Company"
                value={task.companyName}
                href={
                  task.companyId ? `/companies/${task.companyId}` : null
                }
              />
              <LinkedRow
                label="Product"
                value={task.productName}
                href={
                  task.productId ? `/products/${task.productId}` : null
                }
              />
              <LinkedRow
                label="Project"
                value={task.projectName}
                href={
                  task.projectId ? `/projects/${task.projectId}` : null
                }
              />
              <LinkedRow
                label="Meeting"
                value={
                  task.meetingTitle
                    ? `${task.meetingCode ?? ""} ${task.meetingTitle}`.trim()
                    : null
                }
                href={
                  task.meetingId ? `/meetings/${task.meetingId}` : null
                }
              />
              <LinkedRow
                label="Decision"
                value={
                  task.decisionTitle
                    ? `${task.decisionCode ?? ""} ${task.decisionTitle}`.trim()
                    : null
                }
                href={
                  task.decisionId ? `/decisions/${task.decisionId}` : null
                }
              />
              <LinkedRow
                label="Parent Idea"
                value={
                  task.ideaTitle
                    ? `${task.ideaCode ?? ""} ${task.ideaTitle}`.trim()
                    : null
                }
                href={task.ideaId ? `/ideas/${task.ideaId}` : null}
              />
              <LinkedRow
                label="Milestone"
                value={
                  task.milestoneTitle
                    ? `${task.milestoneCode ?? ""} ${task.milestoneTitle}`.trim()
                    : null
                }
                href={
                  task.milestoneId
                    ? `/milestones/${task.milestoneId}`
                    : null
                }
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Owner
              </div>
              {editMode ? (
                <select
                  value={editAssignedToId}
                  onChange={(e) => setEditAssignedToId(e.target.value)}
                  className="mt-1 flex h-8 w-full rounded-md border border-border bg-background px-2 py-1 text-sm"
                >
                  <option value="">Unassigned</option>
                  {allUsers.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </select>
              ) : (
                <div className="mt-0.5 text-foreground">
                  {task.assignedToName ?? "Unassigned"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Status
              </div>
              {editMode ? (
                <Select
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as TaskStatus)}
                >
                  {STATUS_LIST.map((s) => (
                    <option key={s.value} value={s.value}>
                      {s.label}
                    </option>
                  ))}
                </Select>
              ) : (
                <div className="mt-1">
                  <TaskStatusBadge status={task.status} />
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Priority
              </div>
              {editMode ? (
                <Select
                  value={editPriority}
                  onChange={(e) =>
                    setEditPriority(e.target.value as TaskPriority)
                  }
                >
                  <option value="CRITICAL">Critical</option>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </Select>
              ) : (
                <div className="mt-1">
                  <TaskPriorityBadge priority={task.priority} />
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Created By
              </div>
              <div className="mt-0.5 text-foreground">
                {task.createdByName ?? "—"}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Est. Hours
              </div>
              {editMode ? (
                <Input
                  type="number"
                  step="0.25"
                  value={editEstHours}
                  onChange={(e) => setEditEstHours(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {task.estimatedHours != null ? task.estimatedHours : "—"}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Phase / Stage
              </div>
              {editMode ? (
                <PhaseStagePicker
                  projectId={editProjectId}
                  phaseId={editPhaseId}
                  stageId={editStageId}
                  onPhaseChange={setEditPhaseId}
                  onStageChange={setEditStageId}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {task.phaseName ?? "—"}
                  {task.stageName ? ` / ${task.stageName}` : ""}
                </div>
              )}
            </div>
          </div>

          <div className="mt-4">
            <div className="text-[11px] uppercase text-muted-foreground">
              Description
            </div>
            {editMode ? (
              <textarea
                rows={3}
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                className="mt-1 flex w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
              />
            ) : (
              <div className="mt-1 whitespace-pre-wrap text-sm text-foreground">
                {task.description ?? "—"}
              </div>
            )}
          </div>

          {editMode && (
            <div className="mt-4 border-t pt-4" style={{ borderColor: "hsl(210,30%,90%)" }}>
              <AssociationFields
                companyId={editCompanyId}
                productId={editProductId}
                projectId={editProjectId}
                meetingId={editMeetingId}
                decisionId={editDecisionId}
                ideaId={editIdeaId}
                milestoneId={editMilestoneId}
                showMeeting
                showDecision
                showIdea
                showMilestone
                onChange={(field, value) => {
                  if (field === "companyId") setEditCompanyId(value);
                  else if (field === "productId") setEditProductId(value);
                  else if (field === "projectId") {
                    setEditProjectId(value);
                    setEditPhaseId(null);
                    setEditStageId(null);
                  } else if (field === "meetingId") setEditMeetingId(value);
                  else if (field === "decisionId") setEditDecisionId(value);
                  else if (field === "ideaId") setEditIdeaId(value);
                  else if (field === "milestoneId") setEditMilestoneId(value);
                }}
              />
            </div>
          )}
        </div>

        {/* RIGHT — Dates & Quick Status */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <h3 className="mb-3 text-[13px] font-semibold text-foreground">
            Dates
          </h3>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Start Date
              </div>
              {editMode ? (
                <Input
                  type="date"
                  value={editStartDate}
                  onChange={(e) => setEditStartDate(e.target.value)}
                />
              ) : (
                <div className="mt-0.5 text-foreground">
                  {formatDate(task.startDate)}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Due Date
              </div>
              {editMode ? (
                <Input
                  type="date"
                  value={editDueDate}
                  onChange={(e) => setEditDueDate(e.target.value)}
                />
              ) : (
                <div
                  className={`mt-0.5 ${
                    overdue
                      ? "text-[hsl(0,70%,45%)] font-medium"
                      : "text-foreground"
                  }`}
                >
                  {formatDate(task.dueDate)}
                </div>
              )}
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Created At
              </div>
              <div className="mt-0.5 text-foreground">
                {formatDateTime(task.createdAt)}
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase text-muted-foreground">
                Updated At
              </div>
              <div className="mt-0.5 text-foreground">
                {formatDateTime(task.updatedAt)}
              </div>
            </div>
            {task.completedAt && (
              <div className="col-span-2">
                <div className="text-[11px] uppercase text-muted-foreground">
                  Completed At
                </div>
                <div className="mt-0.5 text-foreground">
                  {formatDateTime(task.completedAt)}
                </div>
              </div>
            )}
          </div>

          <div
            className="my-4 border-t"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          />

          <div>
            <div className="mb-2 text-[11px] uppercase text-muted-foreground">
              Change Status
            </div>
            <div className="flex flex-wrap gap-2">
              {STATUS_LIST.map((s) => {
                const active = task.status === s.value;
                const disabled =
                  s.value === "COMPLETED" && task.incompleteActionsCount > 0;
                return (
                  <button
                    key={s.value}
                    type="button"
                    disabled={disabled || updateStatus.isPending}
                    onClick={() => handleQuickStatus(s.value)}
                    title={
                      disabled
                        ? `Cannot complete — ${task.incompleteActionsCount} action(s) still open`
                        : undefined
                    }
                    className="inline-flex h-7 items-center rounded-full border px-3 text-[12px] font-medium transition-colors disabled:cursor-not-allowed"
                    style={{
                      background: active
                        ? "hsl(210,100%,45%)"
                        : "transparent",
                      color: active ? "#fff" : "hsl(215,16%,40%)",
                      borderColor: active
                        ? "hsl(210,100%,45%)"
                        : "hsl(210,30%,85%)",
                      opacity: disabled ? 0.5 : 1,
                    }}
                  >
                    {s.label}
                  </button>
                );
              })}
            </div>
            {statusError && (
              <div
                className="mt-2 rounded px-3 py-1.5 text-[12px]"
                style={{
                  background: "hsl(0,70%,96%)",
                  color: "hsl(0,70%,40%)",
                }}
              >
                {statusError}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ROW 3 — Items tabs */}
      <div
        className="mb-4 overflow-hidden rounded-xl border bg-white"
        style={{ borderColor: "hsl(210,30%,85%)" }}
      >
        <div
          className="flex items-center border-b"
          style={{ borderColor: "hsl(210,30%,90%)" }}
        >
          {ITEM_TABS.map((t) => {
            const count =
              t.key === "actions" ? actionStats.total : 0;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className={`flex items-center gap-1.5 border-b-2 px-4 py-2.5 text-sm font-medium transition-colors ${
                  active
                    ? "border-primary text-primary"
                    : "border-transparent text-muted-foreground"
                }`}
              >
                {t.label}
                <span
                  className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
                  style={{
                    background: active
                      ? "hsl(210,55%,92%)"
                      : "hsl(210,16%,93%)",
                    color: active
                      ? "hsl(210,100%,28%)"
                      : "hsl(215,16%,40%)",
                  }}
                >
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        <div className="p-4">
          {tab === "all" && (
            <div className="py-8 text-center text-sm text-muted-foreground">
              {actionStats.total === 0
                ? "No items yet — use the tabs above to add actions, notes, ideas..."
                : "View individual tabs for details."}
            </div>
          )}

          {tab === "actions" && (
            <>
              <div className="mb-3 grid grid-cols-4 gap-3">
                <StatCard label="Total" value={actionStats.total} />
                <StatCard label="Open" value={actionStats.open} />
                <StatCard label="In Progress" value={actionStats.inProgress} />
                <StatCard label="Completed" value={actionStats.completed} />
              </div>
              {showWarning && (
                <div
                  className="mb-3 rounded-lg border px-4 py-2.5 text-sm"
                  style={{
                    background: "hsl(38,90%,94%)",
                    borderColor: "hsl(38,92%,70%)",
                    color: "hsl(32,90%,30%)",
                  }}
                >
                  ⚠ This task has {task.incompleteActionsCount} incomplete
                  action(s). Complete all actions before closing the task.
                </div>
              )}
              <table className="w-full text-left text-sm">
                <thead
                  className="border-b text-[12px] font-semibold text-muted-foreground"
                  style={{ borderColor: "hsl(210,30%,90%)" }}
                >
                  <tr>
                    <th className="px-2 py-2">#</th>
                    <th className="px-2 py-2">Title</th>
                    <th className="px-2 py-2">Priority</th>
                    <th className="px-2 py-2">Status</th>
                    <th className="px-2 py-2">Due Date</th>
                    <th className="px-2 py-2">Assignee</th>
                  </tr>
                </thead>
                <tbody>
                  {actions.length === 0 && (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-2 py-8 text-center text-muted-foreground"
                      >
                        No actions yet
                      </td>
                    </tr>
                  )}
                  {actions.map((a: any, i: number) => (
                    <tr
                      key={a.id}
                      className="border-b"
                      style={{ borderColor: "hsl(210,30%,92%)" }}
                    >
                      <td className="px-2 py-2 font-mono text-[11px] text-muted-foreground">
                        #{i + 1}
                      </td>
                      <td className="px-2 py-2 text-foreground">{a.title}</td>
                      <td className="px-2 py-2">{a.priority}</td>
                      <td className="px-2 py-2">{a.status}</td>
                      <td className="px-2 py-2">{formatDate(a.dueDate)}</td>
                      <td className="px-2 py-2 text-muted-foreground">
                        {a.assignee ?? "—"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-3 flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setActionDrawerOpen(true)}
                >
                  + Create Action
                </Button>
                <Button variant="outline" size="sm" disabled>
                  🔗 Link Existing
                </Button>
              </div>
            </>
          )}

          {tab === "notes" && (
            <NotesTab
              filter={{ taskId: task.id }}
              prefill={{
                taskId: task.id,
                taskTitle: task.title,
                taskCode: task.code,
                companyId: task.companyId,
                productId: task.productId,
                projectId: task.projectId,
                locked: { taskId: true },
              }}
            />
          )}
          {tab === "ideas" && (
            <IdeasTab
              filter={{ taskId: task.id }}
              prefill={{
                taskId: task.id,
                taskTitle: task.title,
                taskCode: task.code,
                companyId: task.companyId,
                productId: task.productId,
                projectId: task.projectId,
                locked: { taskId: true },
              }}
            />
          )}
          {tab === "decisions" && (
            <DecisionsTab
              filter={{
                projectId: task.projectId ?? undefined,
                companyId: task.projectId ? undefined : task.companyId ?? undefined,
              }}
              prefill={{
                companyId: task.companyId,
                productId: task.productId,
                projectId: task.projectId,
                taskId: task.id,
              }}
            />
          )}
          {tab === "milestones" && (
            <div className="space-y-3">
              <div className="flex justify-end">
                <Button
                  size="sm"
                  onClick={() => setMilestoneDrawerOpen(true)}
                >
                  + Create Milestone
                </Button>
              </div>
              <div className="py-8 text-center text-sm text-muted-foreground">
                No milestones linked yet
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ROW 4 — Comments + Attachments */}
      <div
        className="grid gap-4"
        style={{ gridTemplateColumns: "60fr 40fr" }}
      >
        {/* Comments */}
        <div
          className="flex flex-col rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <MessageSquare
              className="h-4 w-4 text-foreground"
              strokeWidth={1.5}
            />
            <h3 className="text-[13px] font-semibold text-foreground">
              Comments
            </h3>
            <span
              className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full px-1.5 text-[10px] font-medium"
              style={{
                background: "hsl(210,16%,93%)",
                color: "hsl(215,16%,40%)",
              }}
            >
              {commentsQuery.data?.length ?? 0}
            </span>
          </div>

          <div className="max-h-[300px] flex-1 overflow-y-auto">
            {(!commentsQuery.data || commentsQuery.data.length === 0) && (
              <div className="py-6 text-center text-sm text-muted-foreground">
                No comments yet. Be the first to comment.
              </div>
            )}
            {[...(commentsQuery.data ?? [])].reverse().map((c) => {
              const isOwn = c.authorId === user?.id;
              return (
                <div
                  key={c.id}
                  className="border-b py-3 last:border-b-0"
                  style={{ borderColor: "hsl(210,30%,93%)" }}
                >
                  <div className="flex items-start gap-2">
                    <span
                      className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
                      style={{
                        background: "hsl(210,55%,92%)",
                        color: "hsl(210,100%,28%)",
                      }}
                    >
                      {initialsOf(c.authorName ?? "??")}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-semibold text-foreground">
                          {c.authorName ?? "Unknown"}
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                          {timeAgo(c.createdAt)}
                        </span>
                        {isOwn && (
                          <button
                            type="button"
                            onClick={() => deleteComment.mutate(c.id)}
                            className="ml-auto text-muted-foreground hover:text-[hsl(0,70%,45%)]"
                            title="Delete"
                          >
                            <Trash2 className="h-3 w-3" strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                      <div className="mt-1 whitespace-pre-wrap text-[14px] text-foreground">
                        {c.content}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div
            className="mt-3 flex items-end gap-2 border-t pt-3"
            style={{ borderColor: "hsl(210,30%,90%)" }}
          >
            <textarea
              rows={2}
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              onKeyDown={(e) => {
                if (e.ctrlKey && e.key === "Enter") {
                  e.preventDefault();
                  submitComment();
                }
              }}
              placeholder="Write a comment... (Ctrl+Enter to submit)"
              className="flex-1 rounded-md border border-border bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
            <button
              type="button"
              onClick={submitComment}
              disabled={!commentText.trim() || addComment.isPending}
              className="flex h-9 w-9 items-center justify-center rounded-full text-white disabled:opacity-50"
              style={{ background: "hsl(210,100%,45%)" }}
              title="Send"
            >
              <Send className="h-3.5 w-3.5" strokeWidth={1.8} />
            </button>
          </div>
        </div>

        {/* Attachments */}
        <div
          className="rounded-xl border bg-white"
          style={{ borderColor: "hsl(210,30%,85%)", padding: "16px 20px" }}
        >
          <div className="mb-3 flex items-center gap-2">
            <Paperclip
              className="h-4 w-4 text-foreground"
              strokeWidth={1.5}
            />
            <h3 className="text-[13px] font-semibold text-foreground">
              Attachments
            </h3>
          </div>
          <FileUploadZone
            entityType="task"
            entityId={task.id}
            entityLabel={task.code}
            taskId={task.id}
            companyId={task.companyId ?? undefined}
            productId={task.productId ?? undefined}
            projectId={task.projectId ?? undefined}
            showExisting
            compact
          />
        </div>
      </div>

      <NewTaskDrawer
        isOpen={duplicateOpen}
        onClose={() => setDuplicateOpen(false)}
        duplicateFrom={task}
        onCreated={(code) => showToast(`Task created: ${code}`)}
      />

      <ActivityHistoryDrawer
        isOpen={activityOpen}
        onClose={() => setActivityOpen(false)}
        entityType="task"
        entityId={task.id}
        entityLabel={task.title}
      />

      <NewActionDrawer
        isOpen={actionDrawerOpen}
        onClose={() => setActionDrawerOpen(false)}
        onCreated={(code) => showToast(`Action created: ${code}`)}
        prefill={{
          taskId: task.id,
          taskCode: task.code,
          taskTitle: task.title,
          projectId: task.projectId,
          companyId: task.companyId,
          productId: task.productId,
          locked: {
            taskId: true,
            projectId: !!task.projectId,
            companyId: !!task.companyId,
            productId: !!task.productId,
          },
        }}
      />

      <NewMilestoneDrawer
        isOpen={milestoneDrawerOpen}
        onClose={() => setMilestoneDrawerOpen(false)}
        onCreated={(code) => showToast(`Milestone created: ${code}`)}
        prefill={{
          projectId: task.projectId,
          companyId: task.companyId,
          productId: task.productId,
        }}
      />

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete task?"
        description={`Are you sure you want to delete '${task.title}'? It will be removed from your Tasks list.`}
        onConfirm={confirmDelete}
      />
    </AppLayout>
  );
}

function StatCard({ label, value }: { label: string; value: number }) {
  return (
    <div
      className="rounded-lg border px-3 py-2 text-center"
      style={{
        borderColor: "hsl(210,30%,90%)",
        background: "hsl(210,30%,98%)",
      }}
    >
      <div className="text-[11px] uppercase text-muted-foreground">{label}</div>
      <div className="mt-0.5 text-lg font-semibold text-foreground">
        {value}
      </div>
    </div>
  );
}

function LinkedRow({
  label,
  value,
  href,
}: {
  label: string;
  value: string | null;
  href?: string | null;
}) {
  return (
    <div className="flex gap-2">
      <span className="w-24 shrink-0 text-muted-foreground">{label}:</span>
      {value ? (
        href ? (
          <Link to={href} className="text-foreground hover:underline">
            {value}
          </Link>
        ) : (
          <span className="text-foreground">{value}</span>
        )
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </div>
  );
}
